/**
 * Shared AOTC components and helpers — used by the Beneficiary(ies) page
 * (beneficiary card + Add/Edit dialog) and credit calculation across the app.
 */
import { money } from "@/lib/format";
import React from "react";
import { fmtAccounting, MoneyInput } from "@/lib/accountingFormat";

// ── Credit calculation ──────────────────────────────────────────────
export function aotcCredit(reserve) {
  if (reserve <= 0) return 0;
  let c = Math.min(reserve, 2000);
  if (reserve > 2000) c += 0.25 * Math.min(reserve - 2000, 2000);
  return Math.round(c * 100) / 100;
}

// ── AotcYesPanel — reserve control shown when eligibility is "yes" ──
export function AotcYesPanel({ aotcManual, setAotcManual, studentName, taxYear, aotcCap, capUnconfirmed, reserveRef }) {
  const MAX = aotcCap || 4000;
  const isBlank = aotcManual === "" || aotcManual === null || aotcManual === undefined;
  const amt = isBlank ? 0 : (parseFloat(aotcManual) || 0);
  const credit = aotcCredit(amt);
  const suggestedCredit = aotcCredit(MAX);

  // The shared box decides what may be typed; this only keeps the reserve at
  // or under the cap, as it always has.
  const handleChange = (v) => {
    const n = parseFloat(v);
    setAotcManual(!isNaN(n) && n > MAX ? String(MAX) : v);
  };

  return (
    <div className="mt-3 rounded-md border border-[#077A39]/30 bg-[#077A39]/5 px-3 py-3 text-xs" data-testid="aotc-yes-panel">
      {/* Labeled reserve control */}
      <label className="block" data-testid="aotc-reserve-control">
        <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-[#374151]">
          AOTC reserve for {studentName}, tax year {taxYear}
        </span>
        <MoneyInput currency="USD" ref={reserveRef}
          value={isBlank ? "" : aotcManual}
          onChange={handleChange}
          placeholder={`${fmtAccounting(MAX)} (max for full credit)`}
          testid="aotc-reserve-input" />
        <p className="mt-1 text-[11px] text-[#6b7280]">
          The amount of out-of-pocket 529-eligible expenses you&rsquo;re setting aside to claim the American Opportunity Tax Credit — up to ${MAX.toLocaleString()} per student per tax year.
        </p>
        {isBlank && (
          <p className="mt-0.5 text-[10px] text-[#6b7280]">No amount reserved yet. Enter an amount and save to activate your AOTC reserve. Reserving ${MAX.toLocaleString()} earns the full ${suggestedCredit.toLocaleString()} credit.</p>
        )}
        {capUnconfirmed && <p className="mt-0.5 text-[10px] text-[#D97706]">The ${MAX.toLocaleString()} cap for tax year {taxYear} has not yet been confirmed by the IRS — verify before filing.</p>}
      </label>

      {/* Credit feedback */}
      <div className="mt-3 rounded-md border border-[#077A39]/20 bg-white px-3 py-2" data-testid="aotc-credit-feedback">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[#6b7280]">Reserve:</span>
          <span className="font-bold text-[#0A0A0A]">{money(amt, "USD")}</span>
          <span className="text-[#6b7280]">&rarr;</span>
          <span className="text-[#6b7280]">Estimated credit:</span>
          <span className="font-extrabold text-[#077A39]">{money(credit, "USD")}</span>
        </div>
        {amt > MAX && <p className="mt-1 text-[10px] text-[#D97706]">Amounts above ${MAX.toLocaleString()} don&rsquo;t increase the AOTC.</p>}
      </div>

      <p className="mt-2 text-[#374151]">Reserve tuition/fees/course-materials expenses from 529 allocation for the AOTC credit.</p>
    </div>
  );
}
